import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { AccessToken } from '@squareup/dex-types-shared-access-token';
import { ApplicationType } from '@squareup/dex-types-shared-application-type';
import { OAuthClient } from '@squareup/dex-types-shared-oauth-client';
import {
  SandboxAuthorization,
  SandboxTestAccount,
} from '@squareup/dex-types-shared-sandbox-test-account';
import { useEffect, useState } from 'react';

type AccessTokensData = {
  [ApplicationType.Sandbox]:
    | {
        merchants: SandboxTestAccount[];
        oauthClients: (OAuthClient & { production_client: string })[];
        accessTokens: AccessToken[];
        authorizations: SandboxAuthorization[];
      }
    | undefined;
  [ApplicationType.Production]:
    | {
        oauthClients: OAuthClient[];
        accessTokens: AccessToken[];
      }
    | undefined;
};

/**
 * Transforms the names of sandbox OAuth clients to match the names of production OAuth clients
 * @param sandboxOauthClients List of sandbox OAuth clients
 * @param prodOauthClients List of production OAuth clients
 * @returns List of sandbox OAuth clients with names transformed
 */
const transformSandboxOauthClients = (
  sandboxOauthClients: OAuthClient[] | undefined,
  prodOauthClients: OAuthClient[] | undefined
) => {
  const SANDBOX_CLIENT_NAME_PREFIX = 'Sandbox for ';
  if (!sandboxOauthClients || !prodOauthClients) {
    return [];
  }

  return sandboxOauthClients.map((sandboxClient) => {
    const prodClient = prodOauthClients.find(
      (prodClient) =>
        sandboxClient.name.replace(SANDBOX_CLIENT_NAME_PREFIX, '') ===
        prodClient.id
    );
    return {
      ...sandboxClient,
      name: prodClient?.name || sandboxClient.name,
      production_client: prodClient?.id || '',
    };
  });
};

interface AccessTokenAPIError {
  message: string;
  status: string;
  endpoint: string;
  param: string;
}

/**
 * Hook to fetch access token data for a given application type
 * @param appType ApplicationType (Sandbox or Production)
 * @returns { data, isLoading, error } Access token data, loading state, and error
 */
const useAccessTokens = (
  options: { skip?: boolean } = {}
): {
  data: AccessTokensData | undefined;
  isLoading: boolean;
  errors: AccessTokenAPIError[];
} => {
  const { skip } = options;

  const [data, setData] = useState<AccessTokensData>({
    [ApplicationType.Sandbox]: undefined,
    [ApplicationType.Production]: undefined,
  });
  const [errors, setErrors] = useState<AccessTokenAPIError[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchMerchants] = getDeveloperApi().useLazyGetSandboxMerchantsQuery();
  const [fetchSandboxAuthorizations] =
    getDeveloperApi().useLazyGetSandboxAuthorizationsQuery();
  const [fetchAccessTokens] = getDeveloperApi().useLazyGetAccessTokensQuery();
  const [fetchOauthClients] = getDeveloperApi().useLazyGetOauthClientsQuery();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      const { data: merchantData } = await fetchMerchants(undefined);
      // Fetch sandbox authorizations for each merchant
      const sandboxAuthData = await Promise.all(
        merchantData?.accounts
          ?.filter((merchant) => !merchant.is_primary)
          .map((merchant) => fetchSandboxAuthorizations(merchant.token)) || []
      );

      const [
        sandboxOauthClientData,
        prodOauthClientData,
        sandboxAccessTokenData,
        prodAccessTokenData,
      ] = await Promise.all([
        fetchOauthClients(ApplicationType.Sandbox),
        fetchOauthClients(ApplicationType.Production),
        fetchAccessTokens(ApplicationType.Sandbox),
        fetchAccessTokens(ApplicationType.Production),
      ]);

      const apiErrors: AccessTokenAPIError[] = [
        sandboxOauthClientData,
        prodOauthClientData,
        sandboxAccessTokenData,
        prodAccessTokenData,
      ].reduce((apiErrors: AccessTokenAPIError[], data) => {
        if (data.isError) {
          const { error, endpointName: endpoint, originalArgs } = data;
          apiErrors.push({
            endpoint,
            param: originalArgs,
            message: (error as { error: string }).error,
            // isRbacPermissionError(error) compares status code as string
            status: `${(error as { originalStatus: number }).originalStatus}`,
          });
        }
        return apiErrors;
      }, []);

      apiErrors.length > 0 && setErrors(apiErrors);

      // Transform sandbox OAuth client names to match production OAuth client names
      const sandboxOauthClients = transformSandboxOauthClients(
        sandboxOauthClientData.data?.['oauth-clients'],
        prodOauthClientData.data?.['oauth-clients']
      );

      // Merge sandbox authorizations from each merchant
      const sandboxAuths = sandboxAuthData
        .flatMap((auth) => auth.data?.['sandbox-authorizations'])
        .filter((auth): auth is SandboxAuthorization => auth !== undefined);

      setIsLoading(false);
      setData({
        [ApplicationType.Production]: {
          oauthClients: prodOauthClientData.data?.['oauth-clients'] || [],
          accessTokens: prodAccessTokenData.data?.['access-tokens'] || [],
        },
        [ApplicationType.Sandbox]: {
          // Include merchant and authorization data for sandbox apps
          merchants: merchantData?.accounts || [],
          oauthClients: sandboxOauthClients || [],
          accessTokens: sandboxAccessTokenData.data?.['access-tokens'] || [],
          authorizations: sandboxAuths,
        },
      });
    }

    !skip && fetchData();
  }, [
    skip,
    fetchMerchants,
    fetchSandboxAuthorizations,
    fetchAccessTokens,
    fetchOauthClients,
  ]);

  return { data, isLoading, errors };
};

export { useAccessTokens, type AccessTokensData, type AccessTokenAPIError };
